import { useState } from 'react';
import { useLocation } from 'react-router';
import * as hooks from '../../hooks';
import { IconRender } from '../IconRender';
import TextFilter from './TextFilter';
import FilterDropdown from './FilterDropdown';
import SelectedFilters from './SelectedFilters';
import DateFilter from './DateFilter';
import CustomDateFilter from './CustomDateFilter';
import SingleDateFilter from './SingleDateFilter';
import MultiSelectFilter from './MultiSelectFilter';

export interface DateFilterOptions {
    dateDefaultKey?: string;
    datePickerIconPosition?: 'Right' | 'Left';
    datePickerIconType?: 'FontAwesome Icon' | 'Image';
    dateFormat?: string;
    datelabel?: string;
    disableLabelTabIndex?: boolean;
    startDatelabel?: string;
    endDatelabel?: string;
    datePickerIcon?: string;
    datePickerImage?: string;
    dataKey?: string;
    limitDateMinToMinDate?: boolean;
    limitDateMaxToCurrentDate?: boolean;
    limitDateMaxToMaxDate?: boolean;
    limitStartDateMinToMinDate?: boolean;
    limitStartDateMaxToCurrentDate?: boolean;
    limitStartDateMaxToMaxDate?: boolean;
    limitEndDateMinToStartDate?: boolean;
    limitEndDateMaxToCurrentDate?: boolean;
    limitCurrentDaySelection?: boolean;
    limitEndDateMaxToMaxDate?: boolean;
    startDateErrorMessage?: string;
    endDateErrorMessage?: string;
    errorIconPosition?: 'Right' | 'Left';
    errorIconType?: 'FontAwesome Icon' | 'Image';
    errorIcon?: string;
    errorImageUrl?: string;
    numberOfLettersForTheDays?: string;
    clearFilters?: boolean;
    closeDatePickerIconPosition?: 'Right' | 'Left';
    closeDatePickerIconType?: 'FontAwesome Icon' | 'Image';
    closeDatePickerIcon?: string;
    closeDatePickerImage?: string;
    backArrowIconType?: 'FontAwesome Icon' | 'Image';
    backArrowIcon?: string;
    backArrowImage?: string;
    forwardArrowIconType?: 'FontAwesome Icon' | 'Image';
    forwardArrowIcon?: string;
    forwardArrowImage?: string;
    limitDatekeySelection?: string;
    limitDateStartErrorMessage?: string;
    limitDateEndErrorMessage?: string;
    limitDateErrorMessage?: string;
    dateErrorMessage?: string;
}

export interface Filter extends DateFilterOptions {
    label?: string;
    placeholder?: string;
    type?:
        | ''
        | 'Text'
        | 'Dropdown'
        | 'Date range picker'
        | 'Custom Date range picker'
        | 'Custom Date picker'
        | 'Multi Select';
    filterBy?: string;
    filterByKey?: string;
    startDateFilterByKey?: string;
    endDateFilterByKey?: string;
    dateFilterByKey?: string;
    defaultValue?: string;
    urlParam?: string;
    showSearchIcon?: boolean;
    showCancelIcon?: boolean;
    searchIconPosition?: 'Right' | 'Left';
    searchIconType?: '' | 'FontAwesome Icon' | 'Image';
    searchIcon?: string;
    searchImageUrl?: string;
    cancelIconType?: '' | 'FontAwesome Icon' | 'Image';
    cancelIcon?: string;
    cancelImageUrl?: string;
    dropdownDataKey?: string;
    displayLabelsKey?: string;
    dropdownType?: '' | 'Single Select' | 'Multi Select';
    includeAllOption?: boolean;
    includeAllOptionLabel?: string;
    closeOnScroll?: boolean;
    dropdownItemSelectIconPosition?: 'Right' | 'Left';
    dropdownItemSelectIconType?: '' | 'FontAwesome Icon' | 'Image';
    dropdownItemSelectIcon?: string;
    dropdownItemSelectImage?: string;
    dropdownIconType?: '' | 'FontAwesome Icon' | 'Image';
    dropdownOpenIcon?: string;
    dropdownCloseIcon?: string;
    dropdownIconPosition?: 'Right' | 'Left';
    dropdownOpenImage?: string;
    dropdownCloseImage?: string;
    enableDDSearch?: boolean;
    showDDSearchIcon?: boolean;
    serachDDIconPosition?: 'Right' | 'Left';
    searchDDIconType?: 'FontAwesome Icon' | 'Image';
    searchDDIcon?: string;
    searchCancelDDIcon?: string;
    searchDDImageUrl?: string;
    searchCancelDDImageUrl?: string;
    hideDDIconSearch?: boolean;
    searchPlaceholder?: string;
    noOptionFoundText?: string;
    analyticsLabel?: string;
    multiSelectIconPosition?: 'Right' | 'Left';
    multiSelectDataKey?: string;
    optionsLimit?: number;
}

export interface FiltersUI {
    filterLabel?: string;
    clearAllText?: string;
    appliedText?: string;
    resultText?: string;
    resultTextSingle?: string;
    showSelectedFilters?: boolean;
    isCollapsible?: boolean;
    showCollapsibleIcon?: boolean;
    collapsibleIconPosition?: string;
    collapsibleIconType?: 'FontAwesome Icon' | 'Image';
    collapsibleShowIcon?: string;
    collapsibleHideIcon?: string;
    collapsibleShowImageUrl?: string;
    collapsibleHideImageUrl?: string;
    showCollapsibleText?: boolean;
    collapsibleTextPosition?: string;
    collapsibleShowText?: string;
    collapsibleHideText?: string;
    selectedFiltersCollapsible?: boolean;
    strictMatchingEnabled?: boolean;
}

export interface FiltersProps {
    data: any;
    filters?: Filter[];
    filtersUI?: FiltersUI;
    filteredData?: any;
    selectedFilters?: {
        dropdownFilters: { selectedOpts: string[]; filterBy?: string }[];
        multiSelectFilters?: { selectedOpts: string[]; filterBy?: string }[];
    };
    defaultTextValue?: string;
    onChangeTextFilter: (
        value: string,
        filterBy?: string,
        analyticsLabel?: string,
        filterByKey?: string
    ) => void;
    onSelectOption: (
        opts: any,
        filterBy?: any,
        label?: string,
        defaultValue?: string,
        analyticsLabel?: string,
        filterByKey?: string,
        selectAllLabel?: string,
        selectCurrentOption?: string
    ) => void;
    onClearAllFilters: () => void;
    onCancelFilter: (filter: any, filterType: 'dropdown' | 'multiSelect') => void;
    onChangeDateRange: (
        minDate: any,
        maxDate: any,
        filterBy?: string,
        analyticsLabel?: string,
        startDateFilterByKey?: string,
        endDateFilterByKey?: string
    ) => void;
    onChangeSingleDate: (
        date: any,
        _filterBy?: string,
        _analyticsLabel?: string,
        dateFilterByKey?: string
    ) => void;
    staticDataTabIndex?: number;
    isInCustomizedFundList?: boolean;
    onClickTextFilter?: (analyticsLabel?: string) => void;
    onChangeMultiSelect?: (selectedOptions: any[], filterBy?: any, filterByKey?: any) => void;
}

export const Filters = (props: FiltersProps) => {
    const { filters, data, onChangeTextFilter, onSelectOption, filtersUI, ...rest } = props;
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [clearFilters, setClearFilters] = useState(false);
    const [cancelOptionClicks, setCancelOptionClicks] = useState<number>(0);
    const [cancelFilter, setCancelFilter] = useState<{ opt: string; filterBy: string }>();
    const i18n = hooks.useLocalization();
    const location = useLocation();
    const search = location.search;
    const params = new URLSearchParams(search);

    const onClearAllClick = () => {
        setClearFilters(true);
        rest.onClearAllFilters();
        setIsCollapsed(false);
    };

    const onCancelFilter = (
        filter: { opt: string; filterBy: string },
        filterType: 'dropdown' | 'multiSelect'
    ) => {
        setCancelOptionClicks(cancelOptionClicks + 1);
        setCancelFilter(filter);
        rest.onCancelFilter(filter, filterType);
    };

    const renderIcon = () => {
        return (
            <IconRender
                showButton={true}
                iconType={filtersUI?.collapsibleIconType}
                iconClassName={`collapsible-icon ${
                    rest?.selectedFilters &&
                    [
                        ...rest?.selectedFilters?.dropdownFilters,
                        ...(rest?.selectedFilters?.multiSelectFilters || []),
                    ]?.filter((sf: any) => sf?.selectedOpts?.length > 0)?.length === 0
                        ? 'disabled'
                        : ''
                }`}
                iconName={
                    !isCollapsed ? filtersUI?.collapsibleShowIcon : filtersUI?.collapsibleHideIcon
                }
                imageUrl={
                    !isCollapsed
                        ? filtersUI?.collapsibleShowImageUrl
                        : filtersUI?.collapsibleHideImageUrl
                }
                imageClassName={`collapsible-image ${
                    rest?.selectedFilters &&
                    [
                        ...rest?.selectedFilters?.dropdownFilters,
                        ...(rest?.selectedFilters?.multiSelectFilters || []),
                    ]?.filter((sf: any) => sf?.selectedOpts?.length > 0).length === 0
                        ? 'disabled'
                        : ''
                }`}
                imageAlt={'collapsible'}
                aria-label="collapsible-icon"
                onClick={() =>
                    rest?.selectedFilters &&
                    [
                        ...rest?.selectedFilters?.dropdownFilters,
                        ...(rest?.selectedFilters?.multiSelectFilters || []),
                    ]?.filter((sf: any) => sf?.selectedOpts?.length > 0)?.length > 0 &&
                    setIsCollapsed(!isCollapsed)
                }
                onKeyDown={(e: any) =>
                    e.key === 'Enter' &&
                    rest?.selectedFilters &&
                    [
                        ...rest?.selectedFilters?.dropdownFilters,
                        ...(rest?.selectedFilters?.multiSelectFilters || []),
                    ]?.filter((sf: any) => sf?.selectedOpts?.length > 0)?.length > 0 &&
                    setIsCollapsed(!isCollapsed)
                }
            />
        );
    };

    const renderText = () => (
        <span
            tabIndex={0}
            aria-label={'collapsible-text'}
            className={`collapsible-text ${
                rest?.selectedFilters &&
                [
                    ...rest?.selectedFilters?.dropdownFilters,
                    ...(rest?.selectedFilters?.multiSelectFilters || []),
                ]?.filter((sf: any) => sf?.selectedOpts?.length > 0)?.length === 0
                    ? 'disabled'
                    : ''
            }`}
            onClick={() =>
                rest?.selectedFilters &&
                rest?.selectedFilters &&
                [
                    ...rest?.selectedFilters?.dropdownFilters,
                    ...(rest?.selectedFilters?.multiSelectFilters || []),
                ]?.filter((sf: any) => sf?.selectedOpts?.length > 0)?.length > 0 &&
                setIsCollapsed(!isCollapsed)
            }
            onKeyDown={(e) =>
                e.key === 'Enter' &&
                rest?.selectedFilters &&
                rest?.selectedFilters &&
                [
                    ...rest?.selectedFilters?.dropdownFilters,
                    ...(rest?.selectedFilters?.multiSelectFilters || []),
                ]?.filter((sf: any) => sf?.selectedOpts?.length > 0)?.length > 0 &&
                setIsCollapsed(!isCollapsed)
            }
        >
            {!isCollapsed
                ? i18n.format(filtersUI?.collapsibleShowText, data)
                : i18n.format(filtersUI?.collapsibleHideText, data)}
        </span>
    );

    return (
        <div className="filters">
            <div className="header">
                {filtersUI?.showCollapsibleIcon &&
                    filtersUI?.collapsibleIconPosition === 'Left' &&
                    renderIcon()}
                {filtersUI?.showCollapsibleText &&
                    filtersUI?.collapsibleTextPosition === 'Left' &&
                    renderText()}
                {filtersUI?.filterLabel ? (
                    <span className="filters-label" tabIndex={rest.staticDataTabIndex}>
                        {i18n.format(filtersUI?.filterLabel, data)}
                    </span>
                ) : null}
                {rest?.selectedFilters && filtersUI?.appliedText && (
                    <>
                        {(['dropdownFilters', 'multiSelectFilters'] as const).map((filterKey) => {
                            const filters: any = rest?.selectedFilters?.[filterKey];
                            if (filters?.length) {
                                const totalSelected = filters.reduce(
                                    (acc: any, cur: any) => acc + (cur?.selectedOpts?.length || 0),
                                    0
                                );
                                return (
                                    <span
                                        key={filterKey}
                                        className="filters-applied-text"
                                        tabIndex={rest.staticDataTabIndex}
                                    >
                                        {`${totalSelected} ${i18n.format(filtersUI?.appliedText, data)}`}
                                    </span>
                                );
                            }
                            return null;
                        })}
                    </>
                )}
                {filtersUI?.showCollapsibleIcon &&
                    filtersUI?.collapsibleIconPosition === 'Right' &&
                    renderIcon()}
                {filtersUI?.showCollapsibleText &&
                    filtersUI?.collapsibleTextPosition === 'Right' &&
                    renderText()}
                {filtersUI?.clearAllText ? (
                    <span
                        className="clear-filters"
                        onClick={onClearAllClick}
                        onKeyDown={(e) => (e.key === 'Enter' ? onClearAllClick() : null)}
                        tabIndex={0}
                    >
                        {i18n.format(filtersUI?.clearAllText, data)}
                    </span>
                ) : null}
            </div>
            <div className="filters-container">
                {filters?.map((filter: Filter, i: number) => (
                    <div
                        className={`filter ${
                            filter?.type === 'Text'
                                ? 'text-filter'
                                : filter?.type === 'Dropdown'
                                  ? 'dropdown-filter'
                                  : filter?.type === 'Date range picker'
                                    ? 'date-range-picker-filter'
                                    : filter?.type === 'Custom Date range picker'
                                      ? 'date-range-picker-filter'
                                      : ''
                        }`}
                        key={i}
                    >
                        {filter?.type === 'Text' ? (
                            <TextFilter
                                label={filter?.label || ''}
                                placeholder={filter?.placeholder}
                                onChange={(v: any) => {
                                    onChangeTextFilter(
                                        v,
                                        filter?.filterBy,
                                        i18n.format(filter?.analyticsLabel, data),
                                        filter?.filterByKey
                                    );
                                }}
                                data={data}
                                showSearchIcon={filter?.showSearchIcon}
                                showCancelIcon={filter?.showCancelIcon}
                                searchIconPosition={filter?.searchIconPosition}
                                searchIconType={filter?.searchIconType}
                                searchIcon={filter?.searchIcon}
                                searchImageUrl={filter?.searchImageUrl}
                                cancelIconType={filter?.cancelIconType}
                                cancelIcon={filter?.cancelIcon}
                                cancelImageUrl={filter?.cancelImageUrl}
                                selectedValue={params.get(filter?.urlParam || '')}
                                defaultValue={rest?.defaultTextValue}
                                clearFilters={clearFilters}
                                staticDataTabIndex={rest?.staticDataTabIndex}
                                onClick={() => {
                                    rest?.onClickTextFilter &&
                                        rest.onClickTextFilter(
                                            i18n.format(filter?.analyticsLabel, data)
                                        );
                                }}
                            />
                        ) : filter?.type === 'Dropdown' ? (
                            <FilterDropdown
                                data={data}
                                label={filter?.label || ''}
                                clearDropdownFilters={clearFilters}
                                placeholder={filter?.placeholder}
                                dropdownDataKey={filter?.dropdownDataKey}
                                displayLabelsKey={filter?.displayLabelsKey}
                                dropdownType={
                                    filter?.dropdownType === ''
                                        ? 'Single Select'
                                        : filter?.dropdownType
                                }
                                includeAllOption={filter?.includeAllOption}
                                includeAllOptionLabel={filter?.includeAllOptionLabel}
                                dropdownItemSelectIconPosition={
                                    filter?.dropdownItemSelectIconPosition
                                }
                                dropdownItemSelectIconType={filter?.dropdownItemSelectIconType}
                                dropdownItemSelectIcon={filter?.dropdownItemSelectIcon}
                                dropdownItemSelectImage={filter?.dropdownItemSelectImage}
                                dropdownIconType={filter?.dropdownIconType}
                                dropdownOpenIcon={filter?.dropdownOpenIcon}
                                dropdownCloseIcon={filter?.dropdownCloseIcon}
                                dropdownIconPosition={filter?.dropdownIconPosition}
                                dropdownOpenImage={filter?.dropdownOpenImage}
                                dropdownCloseImage={filter?.dropdownCloseImage}
                                onSelectOption={(opts: any, selectCurrentOption?: string) =>
                                    onSelectOption(
                                        opts,
                                        filter?.filterBy,
                                        i18n.format(filter?.label, data),
                                        i18n.format(filter?.defaultValue, data),
                                        i18n.format(filter?.analyticsLabel, data),
                                        filter?.filterByKey,
                                        filter?.dropdownType === 'Single Select'
                                            ? i18n.format(filter?.includeAllOptionLabel, data) ||
                                                  'All'
                                            : undefined,
                                        selectCurrentOption
                                    )
                                }
                                // pass this state on cancel click to trigger effect
                                // even with identical canceledOption prop
                                cancelOptionClicks={cancelOptionClicks}
                                canceledOption={
                                    filter?.filterBy === cancelFilter?.filterBy ||
                                    filter?.filterByKey === cancelFilter?.filterBy
                                        ? cancelFilter?.opt
                                        : ''
                                }
                                selectedValue={
                                    params.get(filter?.urlParam || '') ||
                                    i18n.format(filter?.defaultValue, data) ||
                                    ''
                                }
                                resetClear={() => setClearFilters(false)}
                                enableSearch={filter?.enableDDSearch}
                                searchPlaceholder={filter?.searchPlaceholder}
                                noOptionFoundText={filter?.noOptionFoundText}
                                showSearchIcon={filter?.showDDSearchIcon}
                                serachIconPosition={filter?.serachDDIconPosition}
                                searchIconType={filter?.searchDDIconType}
                                searchIcon={filter?.searchDDIcon}
                                searchCancelIcon={filter?.searchCancelDDIcon}
                                searchImageUrl={filter?.searchDDImageUrl}
                                searchCancelImageUrl={filter?.searchCancelDDImageUrl}
                                hideDDIconSearch={filter?.hideDDIconSearch}
                                staticDataTabIndex={rest?.staticDataTabIndex}
                                closeOnScroll={filter?.closeOnScroll}
                            />
                        ) : filter?.type === 'Date range picker' ? (
                            <DateFilter
                                data={data}
                                dataKey={filter?.dataKey}
                                startDatelabel={filter?.startDatelabel}
                                endDatelabel={filter?.endDatelabel}
                                dateFormat={filter?.dateFormat}
                                datePickerIconPosition={filter?.datePickerIconPosition}
                                datePickerIconType={filter?.datePickerIconType}
                                datePickerIcon={filter?.datePickerIcon}
                                datePickerImage={filter?.datePickerImage}
                                limitStartDateMinToMinDate={filter?.limitStartDateMinToMinDate}
                                limitStartDateMaxToCurrentDate={
                                    filter?.limitStartDateMaxToCurrentDate
                                }
                                limitStartDateMaxToMaxDate={filter?.limitStartDateMaxToMaxDate}
                                limitEndDateMinToStartDate={filter?.limitEndDateMinToStartDate}
                                limitEndDateMaxToCurrentDate={filter?.limitEndDateMaxToCurrentDate}
                                limitEndDateMaxToMaxDate={filter?.limitEndDateMaxToMaxDate}
                                onChangeDateRange={(minDate, maxDate) =>
                                    rest.onChangeDateRange(
                                        minDate,
                                        maxDate,
                                        filter?.filterBy,
                                        i18n.format(filter?.analyticsLabel, data),
                                        filter?.startDateFilterByKey,
                                        filter?.endDateFilterByKey
                                    )
                                }
                                startDateErrorMessage={filter?.startDateErrorMessage}
                                endDateErrorMessage={filter?.endDateErrorMessage}
                                errorIconPosition={filter?.errorIconPosition}
                                errorIconType={filter?.errorIconType}
                                errorIcon={filter?.errorIcon}
                                errorImageUrl={filter?.errorImageUrl}
                                numberOfLettersForTheDays={filter?.numberOfLettersForTheDays}
                                clearFilters={clearFilters}
                                staticDataTabIndex={rest?.staticDataTabIndex}
                                limitDatekeySelection={filter?.limitDatekeySelection}
                            />
                        ) : filter?.type === 'Custom Date range picker' ? (
                            <CustomDateFilter
                                data={data}
                                dataKey={filter?.dataKey}
                                startDatelabel={filter?.startDatelabel}
                                endDatelabel={filter?.endDatelabel}
                                dateFormat={filter?.dateFormat}
                                datePickerIconPosition={filter?.datePickerIconPosition}
                                datePickerIconType={filter?.datePickerIconType}
                                datePickerIcon={filter?.datePickerIcon}
                                datePickerImage={filter?.datePickerImage}
                                limitStartDateMinToMinDate={filter?.limitStartDateMinToMinDate}
                                limitStartDateMaxToCurrentDate={
                                    filter?.limitStartDateMaxToCurrentDate
                                }
                                limitStartDateMaxToMaxDate={filter?.limitStartDateMaxToMaxDate}
                                limitEndDateMinToStartDate={filter?.limitEndDateMinToStartDate}
                                limitEndDateMaxToCurrentDate={filter?.limitEndDateMaxToCurrentDate}
                                limitEndDateMaxToMaxDate={filter?.limitEndDateMaxToMaxDate}
                                onChangeDateRange={(minDate, maxDate) =>
                                    rest.onChangeDateRange(
                                        minDate,
                                        maxDate,
                                        filter?.filterBy,
                                        i18n.format(filter?.analyticsLabel, data),
                                        filter?.startDateFilterByKey,
                                        filter?.endDateFilterByKey
                                    )
                                }
                                startDateErrorMessage={filter?.startDateErrorMessage}
                                endDateErrorMessage={filter?.endDateErrorMessage}
                                errorIconPosition={filter?.errorIconPosition}
                                errorIconType={filter?.errorIconType}
                                errorIcon={filter?.errorIcon}
                                errorImageUrl={filter?.errorImageUrl}
                                numberOfLettersForTheDays={filter?.numberOfLettersForTheDays}
                                clearFilters={clearFilters}
                                staticDataTabIndex={rest?.staticDataTabIndex}
                                closeDatePickerIconPosition={filter?.closeDatePickerIconPosition}
                                closeDatePickerIconType={filter?.closeDatePickerIconType}
                                closeDatePickerIcon={filter?.closeDatePickerIcon}
                                closeDatePickerImage={filter?.closeDatePickerImage}
                                backArrowIconType={filter?.backArrowIconType}
                                backArrowIcon={filter?.backArrowIcon}
                                backArrowImage={filter?.backArrowImage}
                                forwardArrowIconType={filter?.forwardArrowIconType}
                                forwardArrowIcon={filter?.forwardArrowIcon}
                                forwardArrowImage={filter?.forwardArrowImage}
                                limitDatekeySelection={filter?.limitDatekeySelection}
                                limitDateStartErrorMessage={filter?.limitDateStartErrorMessage}
                                limitDateEndErrorMessage={filter?.limitDateEndErrorMessage}
                            />
                        ) : filter?.type === 'Custom Date picker' ? (
                            <SingleDateFilter
                                data={data}
                                dataKey={filter?.dataKey}
                                datelabel={filter?.datelabel}
                                disableLabelTabIndex={filter?.disableLabelTabIndex}
                                dateFormat={filter?.dateFormat}
                                datePickerIconPosition={filter?.datePickerIconPosition}
                                datePickerIconType={filter?.datePickerIconType}
                                datePickerIcon={filter?.datePickerIcon}
                                datePickerImage={filter?.datePickerImage}
                                limitDateMinToMinDate={filter?.limitDateMinToMinDate}
                                limitDateMaxToCurrentDate={filter?.limitDateMaxToCurrentDate}
                                limitDateMaxToMaxDate={filter?.limitDateMaxToMaxDate}
                                onChangeSingleDate={(date: any) =>
                                    rest.onChangeSingleDate(
                                        date,
                                        filter?.filterBy,
                                        '',
                                        filter?.dateFilterByKey
                                    )
                                }
                                dateDefaultKey={filter?.dateDefaultKey}
                                dateErrorMessage={filter?.dateErrorMessage}
                                errorIconPosition={filter?.errorIconPosition}
                                errorIconType={filter?.errorIconType}
                                errorIcon={filter?.errorIcon}
                                errorImageUrl={filter?.errorImageUrl}
                                staticDataTabIndex={rest?.staticDataTabIndex}
                                closeDatePickerIconPosition={filter?.closeDatePickerIconPosition}
                                closeDatePickerIconType={filter?.closeDatePickerIconType}
                                closeDatePickerIcon={filter?.closeDatePickerIcon}
                                closeDatePickerImage={filter?.closeDatePickerImage}
                                backArrowIconType={filter?.backArrowIconType}
                                backArrowIcon={filter?.backArrowIcon}
                                backArrowImage={filter?.backArrowImage}
                                forwardArrowIconType={filter?.forwardArrowIconType}
                                forwardArrowIcon={filter?.forwardArrowIcon}
                                forwardArrowImage={filter?.forwardArrowImage}
                                limitDatekeySelection={filter?.limitDatekeySelection}
                                limitDateErrorMessage={filter?.limitDateErrorMessage}
                                isInCustomizedFundList={rest?.isInCustomizedFundList}
                            />
                        ) : filter?.type === 'Multi Select' ? (
                            <MultiSelectFilter
                                data={data}
                                label={filter?.label || ''}
                                dataKey={filter?.multiSelectDataKey || ''}
                                onChange={(opts: string[]) =>
                                    rest?.onChangeMultiSelect &&
                                    rest.onChangeMultiSelect(
                                        opts,
                                        filter?.filterBy,
                                        filter?.filterByKey
                                    )
                                }
                                selectedValue={
                                    params.get(filter?.urlParam || '') ||
                                    i18n.format(filter?.defaultValue, data) ||
                                    ''
                                }
                                multiSelectIconPosition={filter?.multiSelectIconPosition || 'Right'}
                                optionsLimit={filter?.optionsLimit || 3}
                                cancelOptionClicks={cancelOptionClicks}
                                canceledOption={
                                    filter?.filterBy === cancelFilter?.filterBy ||
                                    filter?.filterByKey === cancelFilter?.filterBy
                                        ? cancelFilter?.opt
                                        : ''
                                }
                                clearFilters={clearFilters}
                            />
                        ) : null}
                    </div>
                ))}
            </div>
            {rest?.filteredData && filtersUI?.resultText ? (
                <span className="filter-result-text" tabIndex={rest.staticDataTabIndex}>
                    {`${rest?.filteredData?.length} ${
                        rest?.filteredData?.length === 1 && filtersUI?.resultTextSingle
                            ? i18n.format(filtersUI?.resultTextSingle, data)
                            : i18n.format(filtersUI?.resultText, data)
                    }`}
                </span>
            ) : null}
            {filtersUI?.showSelectedFilters && filtersUI?.isCollapsible && isCollapsed ? (
                <>
                    {/* render dropdown filters */}
                    <SelectedFilters
                        selectedFilters={rest?.selectedFilters?.dropdownFilters || []}
                        cancelFilter={(...args) => onCancelFilter(...args, 'dropdown')}
                        staticDataTabIndex={rest?.staticDataTabIndex}
                    />
                    {/* render multi-select filters */}
                    <SelectedFilters
                        selectedFilters={rest?.selectedFilters?.multiSelectFilters || []}
                        cancelFilter={(...args) => onCancelFilter(...args, 'multiSelect')}
                        staticDataTabIndex={rest?.staticDataTabIndex}
                    />
                </>
            ) : null}
        </div>
    );
};

export { filtersStyles } from './Filters.styles';
